import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { NUTRIENT_GROUPS, formatDateTime } from "../lib/nutrition";
import { ArrowLeft, FloppyDisk, Warning } from "@phosphor-icons/react";

export default function IngredientForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = id && id !== "neu";

  const [meta, setMeta] = useState([]);
  const [allergens, setAllergens] = useState([]); // [{key,label}]
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [gtin, setGtin] = useState("");
  const [artikelnummer, setArtikelnummer] = useState("");
  const [nutrients, setNutrients] = useState({});
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [density, setDensity] = useState("");
  const [pieceWeight, setPieceWeight] = useState("");
  const [alcoholVol, setAlcoholVol] = useState("");
  const [containsAlcohol, setContainsAlcohol] = useState(false);
  const [cookingYield, setCookingYield] = useState("");
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const metaByKey = useMemo(() => {
    const m = {};
    meta.forEach((x) => { m[x.key] = x; });
    return m;
  }, [meta]);

  useEffect(() => {
    (async () => {
      const [n, a, c] = await Promise.all([
        api.get("/meta/nutrients"),
        api.get("/meta/allergens"),
        api.get("/ingredients/categories"),
      ]);
      setMeta(n.data);
      setAllergens(a.data);
      setCategories(c.data || []);
      const init = {};
      n.data.forEach((m) => { init[m.key] = 0; });
      setNutrients(init);
      if (isEdit) {
        try {
          const { data: ing } = await api.get(`/ingredients/${id}`);
          setName(ing.name);
          setCategory(ing.category || "");
          setNotes(ing.notes || "");
          setGtin(ing.gtin || "");
          setArtikelnummer(ing.artikelnummer || "");
          setNutrients({ ...init, ...(ing.nutrients || {}) });
          setSelectedAllergens(ing.allergens || []);
          setDensity(ing.density_g_per_ml != null ? String(ing.density_g_per_ml) : "");
          setPieceWeight(ing.piece_weight_g != null ? String(ing.piece_weight_g) : "");
          setAlcoholVol(ing.alcohol_vol_percent != null ? String(ing.alcohol_vol_percent) : "");
          setContainsAlcohol(Boolean(ing.contains_alcohol));
          setCookingYield(ing.cooking_yield_factor != null ? String(ing.cooking_yield_factor) : "");
          setAudit({
            created_by_name: ing.created_by_name, created_at: ing.created_at,
            updated_by_name: ing.updated_by_name, updated_at: ing.updated_at,
          });
        } catch {
          toast.error("Zutat nicht gefunden");
          nav("/zutaten");
        }
      }
    })();
  }, [id, isEdit, nav]);

  const toggleAllergen = (key) => {
    setSelectedAllergens((s) =>
      s.includes(key) ? s.filter((x) => x !== key) : [...s, key]
    );
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Bitte einen Namen angeben");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name, category, notes, nutrients, allergens: selectedAllergens,
        gtin: gtin.trim() || null,
        artikelnummer: artikelnummer.trim() || null,
        density_g_per_ml: density === "" ? null : Number(density),
        piece_weight_g: pieceWeight === "" ? null : Number(pieceWeight),
        alcohol_vol_percent: alcoholVol === "" ? null : Number(alcoholVol),
        contains_alcohol: containsAlcohol,
        cooking_yield_factor: cookingYield === "" ? null : Number(cookingYield),
      };
      if (isEdit) await api.put(`/ingredients/${id}`, body);
      else await api.post("/ingredients", body);
      toast.success(isEdit ? "Zutat aktualisiert" : "Zutat erstellt");
      nav("/zutaten");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ingredient-form-page">
      <div>
        <Link to="/zutaten" className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Zurück
        </Link>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
          {isEdit ? "Zutat bearbeiten" : "Neue Zutat"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Alle Nährwertangaben pro 100&nbsp;g.</p>
      </div>

      <section className="border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm"
              data-testid="ingredient-name-input" placeholder="z. B. Vollmilch 3,5%" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Kategorie</Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 rounded-sm"
              data-testid="ingredient-category-input"
              placeholder="z. B. Milchprodukte"
              list="ingredient-category-options"
              autoComplete="off"
            />
            <datalist id="ingredient-category-options">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <div className="text-[10px] text-zinc-400 mt-0.5">
              Aus Vorschlägen wählen oder neue Kategorie eingeben
            </div>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Notizen</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm"
              data-testid="ingredient-notes-input" placeholder="Optional" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-zinc-100">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">GTIN / EAN</Label>
            <Input
              value={gtin}
              onChange={(e) => setGtin(e.target.value)}
              className="mt-1 rounded-sm font-mono-num"
              data-testid="ingredient-gtin-input"
              placeholder="z. B. 4012345678901"
              maxLength={20}
            />
            <p className="text-[11px] text-zinc-400 mt-1">Globale Artikelnummer (Barcode), optional</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Artikelnummer (intern)</Label>
            <Input
              value={artikelnummer}
              onChange={(e) => setArtikelnummer(e.target.value)}
              className="mt-1 rounded-sm font-mono-num"
              data-testid="ingredient-artikelnummer-input"
              placeholder="z. B. ART-001"
              maxLength={40}
            />
            <p className="text-[11px] text-zinc-400 mt-1">Eigene Artikel-/Bestellnummer, optional</p>
          </div>
        </div>
        {audit && (
          <div className="text-[11px] text-zinc-500 font-mono-num pt-2 border-t border-zinc-100">
            Erstellt von <b>{audit.created_by_name}</b> am {formatDateTime(audit.created_at)}
            {audit.updated_at && <> · zuletzt geändert von <b>{audit.updated_by_name}</b> am {formatDateTime(audit.updated_at)}</>}
          </div>
        )}
      </section>

      <section className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="units-section">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider">
            Mengen-Umrechnung <span className="text-zinc-400 normal-case font-normal">· optional</span>
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Werte hier ausfüllen, damit die Zutat im Rezept auch in <b>ml/Liter</b> oder <b>Stück</b> verwendet werden kann. Liter kann auch ohne Dichte (Annahme 1 g/ml) angegeben werden.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">
              Dichte (g je 1 ml)
            </Label>
            <Input type="number" step="0.01" min="0" placeholder="z. B. 1.03 für Milch"
              value={density} onChange={(e) => setDensity(e.target.value)}
              className="mt-1 rounded-sm font-mono-num" data-testid="ingredient-density-input" />
            <p className="text-[11px] text-zinc-400 mt-1">
              Wasser ≈ 1.00 · Milch ≈ 1.03 · Öl ≈ 0.92 · Honig ≈ 1.42
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">
              Stückgewicht (g je 1 Stück)
            </Label>
            <Input type="number" step="0.1" min="0" placeholder="z. B. 60 für ein Ei"
              value={pieceWeight} onChange={(e) => setPieceWeight(e.target.value)}
              className="mt-1 rounded-sm font-mono-num" data-testid="ingredient-piece-weight-input" />
            <p className="text-[11px] text-zinc-400 mt-1">
              Ei ≈ 60 · Apfel ≈ 180 · Banane ≈ 120 · Zwiebel ≈ 110
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">
              Alkohol (Vol-%)
            </Label>
            <Input type="number" step="0.1" min="0" max="100" placeholder="z. B. 12 für Wein"
              value={alcoholVol} onChange={(e) => {
                setAlcoholVol(e.target.value);
                // Vol-% > 0 → automatisch Checkbox setzen
                if (e.target.value && Number(e.target.value) > 0) setContainsAlcohol(true);
              }}
              className="mt-1 rounded-sm font-mono-num" data-testid="ingredient-alcohol-input" />
            <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer" data-testid="contains-alcohol-toggle">
              <Checkbox
                checked={containsAlcohol}
                onCheckedChange={(v) => setContainsAlcohol(Boolean(v))}
                data-testid="ingredient-contains-alcohol"
              />
              <span className="text-zinc-700">Zutat enthält Alkohol</span>
            </label>
            <p className="text-[11px] text-zinc-400 mt-1">
              Aktivieren, wenn die Zutat Alkohol enthält. Vol-% optional für genaue Berechnung.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 p-5 pt-0 border-t border-zinc-100">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">
              Garausbeute (Endgewicht ÷ Rohgewicht)
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="z. B. 0.70 (Fleisch) oder 2.50 (Nudeln)"
              value={cookingYield}
              onChange={(e) => setCookingYield(e.target.value)}
              className="mt-1 rounded-sm font-mono-num max-w-sm"
              data-testid="ingredient-cooking-yield-input"
            />
            <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
              Wert &lt; 1 = Wasserverlust beim Garen · Wert &gt; 1 = Wasseraufnahme. Beispiele: Hähnchenbrust gegart 0.70 · Rindfleisch 0.75 · Lachs 0.80 · Kartoffel gekocht 0.95 · Nudeln 2.50 · Reis 2.80 · Linsen 2.40 · Haferflocken 2.20. Leer lassen, wenn die Zutat nicht zubereitet wird.
            </p>
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="allergens-section">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
          <Warning size={14} weight="fill" className="text-amber-600" />
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider">
            Allergene <span className="text-zinc-400 normal-case font-normal">· EU VO 1169/2011 Anhang II</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-5">
          {allergens.map((a) => {
            const checked = selectedAllergens.includes(a.key);
            return (
              <label
                key={a.key}
                htmlFor={`allergen-${a.key}`}
                className={`flex items-center gap-2 px-3 py-2 border rounded-sm text-sm cursor-pointer transition-colors ${
                  checked
                    ? "border-amber-500 bg-amber-50 text-amber-900"
                    : "border-zinc-200 hover:bg-zinc-50"
                }`}
                data-testid={`allergen-label-${a.key}`}
              >
                <Checkbox
                  id={`allergen-${a.key}`}
                  checked={checked}
                  onCheckedChange={() => toggleAllergen(a.key)}
                  data-testid={`allergen-${a.key}-checkbox`}
                />
                <span>{a.label}</span>
              </label>
            );
          })}
        </div>
        {selectedAllergens.length === 0 && (
          <div className="px-5 pb-4 text-[11px] text-zinc-500">
            Keine Allergene markiert. Wenn die Zutat keines der 14 EU-Pflichtallergene enthält,
            ist das korrekt — andernfalls bitte alle zutreffenden ankreuzen.
          </div>
        )}
      </section>

      {NUTRIENT_GROUPS.map((grp) => (
        <section key={grp.label} className="border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wider">{grp.label}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-5">
            {grp.keys.map((k) => {
              const m = metaByKey[k];
              if (!m) return null;
              return (
                <div key={k}>
                  <Label className="text-xs text-zinc-600">
                    {m.label} <span className="text-zinc-400">({m.unit})</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={nutrients[k] ?? 0}
                    onChange={(e) => setNutrients((s) => ({ ...s, [k]: e.target.value }))}
                    className="mt-1 rounded-sm font-mono-num"
                    data-testid={`nutrient-${k}-input`}
                  />
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className="flex justify-end gap-2 sticky bottom-0 bg-white py-4 border-t border-zinc-200">
        <Link to="/zutaten"><Button variant="outline" className="rounded-sm">Abbrechen</Button></Link>
        <Button onClick={save} disabled={busy}
          className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="save-ingredient-button">
          <FloppyDisk size={14} className="mr-2" /> {busy ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </div>
  );
}
